import React, { useState } from 'react';
import { useAppSelector } from 'hooks';
import { Col, Row } from 'reactstrap';
import dayjs from 'dayjs';

import { dateDisplayFormat } from '~libs/dayjs';

import { ClickableIcon, InfoRow } from '~components';
import { faRefresh } from '~components/icon';

import { Budget } from '~support-at-home/models';

import AlertBadge from '~support-at-home/forms/budgets/AlertBadge';

import { useRecalculateMutation } from '~support-at-home/actions/budgets';

import { selectClientByID } from '~main/selectors/clients/clients';
import { selectClientBudgetStatuses } from '~support-at-home/selectors/clientBudgetStatuses';
import { selectPricePlanRevisions } from '~support-at-home/selectors/pricePlanRevisions';

interface ComponentProps {
  budgetInfo?: Partial<Budget>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  setFieldTouched: (field: string, isTouched: boolean) => void;
}

const BudgetSummary: React.FC<ComponentProps> = ({ budgetInfo, setFieldValue, setFieldTouched }) => {
  const [recalculate] = useRecalculateMutation();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    client_id,
    client_sah_budget_id,
    price_plan_revision,
    price_plan_revision_id,
    start_date,
    end_date,
    package_fixed,
    sections,
    supplements,
    status_id,
    total_includes_case_management,
    total_amount,
  } = budgetInfo ?? {};
  const { price_plan } = price_plan_revision ?? {};

  const pricePlanRevisions = useAppSelector(selectPricePlanRevisions);
  const statuses = useAppSelector(selectClientBudgetStatuses);

  const getStatusLabel = () => {
    return statuses.find((status) => status.id === status_id)?.name || 'N/A';
  };

  const client = useAppSelector((state) => selectClientByID(state, client_id));
  const currentRevision = pricePlanRevisions.find((rev) => rev.id === price_plan_revision_id);
  const latestRevision =
    pricePlanRevisions.length > 0
      ? pricePlanRevisions.reduce((max, current) => (current.sequence > max.sequence ? current : max))
      : undefined;

  const getRevisionLabel = (lookup: any[], id: number | string | undefined) => {
    if (!id) return '-';
    return currentRevision ? `Rev. ${currentRevision.sequence}` : '-';
  };

  const isLatestRevision = latestRevision === currentRevision;

  const handleRefreshRevision = async () => {
    if (!latestRevision) return;
    setIsRefreshing(true);

    try {
      await recalculate({
        sections,
        price_plan_revision_id: latestRevision.id,
        start_date,
        end_date,
        total_amount,
        total_includes_case_management,
        supplements,
        client_sah_budget_id,
      }).unwrap();
      setFieldValue('price_plan_revision_id', latestRevision.id);
      setFieldTouched('price_plan_revision_id', true);
    } catch (error) {
      console.error('Error recalculating budget with latest revision:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const refreshRevisionIcon = (
    <>
      <ClickableIcon
        id="refresh-revision-btn"
        toolTip="Refresh Revision"
        icon={faRefresh}
        onClick={handleRefreshRevision}
        className="summary-refresh-revision"
        disabled={isRefreshing}
      />
    </>
  );

  const safeFormatDate = (date: string | undefined) => (date ? dayjs(date).format(dateDisplayFormat) : '-');

  return (
    <div className="review-info">
      <Col className="info-section">
        <InfoRow label="Client:" value={client?.full_name ?? ''} />
        <InfoRow label="Package:" value={package_fixed?.name ?? ''} />
        <InfoRow label="Funding Type:" value={package_fixed?.funding_type?.name ?? ''} />
        <InfoRow label="Case Manager:" value={budgetInfo?.client?.case_manager_user?.full_name ?? '-'} />
      </Col>
      <Col className="info-section">
        <Row>
          <Col lg={5} className="fw-bold">
            Price Plan:
          </Col>
          <Col className="d-flex align-items-baseline justify-content-end">
            {isLatestRevision ? null : <AlertBadge showAlert={true} tooltip="Not latest revision" />}
            {`${price_plan?.name ?? '-'} - ${getRevisionLabel(pricePlanRevisions, price_plan_revision_id)}`}{' '}
            {isLatestRevision ? null : <>{refreshRevisionIcon}</>}
          </Col>
        </Row>
        <Row>
          <Col lg={5} className="fw-bold">
            Status:
          </Col>
          <Col className="d-flex align-items-baseline justify-content-end">{getStatusLabel()}</Col>
        </Row>
        <InfoRow label="SAH Budget:" value={client_sah_budget_id ? 'Linked' : 'None'} />
      </Col>
      <Col className="info-section">
        <InfoRow label="Start Date:" value={safeFormatDate(start_date)} />
        <InfoRow label="End Date:" value={safeFormatDate(end_date)} />
      </Col>
    </div>
  );
};

export default BudgetSummary;
